import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import CircularProgress from '@mui/material/CircularProgress';
import 'leaflet/dist/leaflet.css';
import { DhlServicePoint } from 'skladon-types';
import Dialog from 'src/components/Dialog';
import Button from 'src/components/Button';

const MAP_MOVE_DEBOUNCE_MS = 600;
const MIN_MOVE_DISTANCE_DEG = 0.01;
const DEFAULT_ZOOM = 12;

const createPinIcon = (fill: string): L.DivIcon =>
    L.divIcon({
        className: 'dhl-service-point-marker',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41" aria-hidden="true" style="display:block">
            <path fill="${fill}" stroke="#fff" stroke-width="1.5"
              d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>
            <circle cx="12.5" cy="12.5" r="5" fill="#fff"/>
        </svg>`,
    });

const defaultMarkerIcon = createPinIcon('#2A81CB');
const selectedMarkerIcon = createPinIcon('#D40511');

export interface DhlServicePointMapPropsType {
    points: DhlServicePoint[];
    selectedId?: string | null;
    /** Center used when opening the map (typically country capital). */
    initialCenter: [number, number];
    /** When true, fit bounds once after the first batch of mappable points is available. */
    autoFitToPoints?: boolean;
    loading?: boolean;
    onSelect: (servicePointId: string) => void;
    onClose: () => void;
    onSearchByGeo: (latitude: number, longitude: number) => void;
}

const FitBoundsOnce: FC<{
    points: Array<{ lat: number; lng: number }>;
    enabled: boolean;
}> = ({ points, enabled }) => {
    const map = useMap();
    const fittedRef = useRef(false);

    useEffect(() => {
        if (!enabled || fittedRef.current || points.length === 0) {
            return;
        }
        fittedRef.current = true;
        if (points.length === 1) {
            map.setView([points[0].lat, points[0].lng], 14);
            return;
        }
        map.fitBounds(
            L.latLngBounds(points.map((point) => [point.lat, point.lng] as [number, number])),
            { padding: [40, 40] }
        );
    }, [map, points, enabled]);

    return null;
};

const MapMoveSearcher: FC<{
    onSearchByGeo: (latitude: number, longitude: number) => void;
}> = ({ onSearchByGeo }) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSearchRef = useRef<{ lat: number; lng: number } | null>(null);

    useMapEvents({
        moveend: (event) => {
            const center = event.target.getCenter();
            const lat = center.lat;
            const lng = center.lng;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                const last = lastSearchRef.current;
                if (
                    last &&
                    Math.abs(last.lat - lat) < MIN_MOVE_DISTANCE_DEG &&
                    Math.abs(last.lng - lng) < MIN_MOVE_DISTANCE_DEG
                ) {
                    return;
                }
                lastSearchRef.current = { lat, lng };
                onSearchByGeo(lat, lng);
            }, MAP_MOVE_DEBOUNCE_MS);
        },
    });

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return null;
};

const DhlServicePointMap: FC<DhlServicePointMapPropsType> = (props) => {
    const [highlightedId, setHighlightedId] = useState<string | null>(props.selectedId ?? null);

    useEffect(() => {
        setHighlightedId(props.selectedId ?? null);
    }, [props.selectedId]);

    const mappablePoints = useMemo(
        () =>
            props.points.filter(
                (point): point is DhlServicePoint & { latitude: number; longitude: number } =>
                    point.latitude !== null &&
                    point.longitude !== null &&
                    Number.isFinite(point.latitude) &&
                    Number.isFinite(point.longitude)
            ),
        [props.points]
    );

    const highlightedPoint = useMemo(
        () => mappablePoints.find((point) => point.servicePointId === highlightedId) ?? null,
        [mappablePoints, highlightedId]
    );

    const handleConfirmSelect = () => {
        if (!highlightedPoint) {
            return;
        }
        props.onSelect(highlightedPoint.servicePointId);
    };

    return (
        <Dialog
            title={translate('orderDetail.dhlSelectOnMap')}
            onClose={props.onClose}
            maxWidth="lg"
            fullWidth
            actions={
                <>
                    <Button onClick={props.onClose} variant="outlined">
                        {translate('dialog.close')}
                    </Button>
                    <Button onClick={handleConfirmSelect} disabled={!highlightedPoint}>
                        {translate('orderDetail.dhlSelectPoint')}
                    </Button>
                </>
            }
        >
            <div style={{ width: '100%' }}>
                <div style={{ height: 480, width: '100%', position: 'relative' }}>
                    <style>
                        {`.dhl-service-point-marker { background: transparent !important; border: none !important; }`}
                    </style>
                    <MapContainer
                        center={props.initialCenter}
                        zoom={DEFAULT_ZOOM}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitBoundsOnce
                            enabled={Boolean(props.autoFitToPoints)}
                            points={mappablePoints.map((point) => ({
                                lat: point.latitude,
                                lng: point.longitude,
                            }))}
                        />
                        <MapMoveSearcher onSearchByGeo={props.onSearchByGeo} />
                        {mappablePoints.map((point) => {
                            const isSelected = highlightedId === point.servicePointId;
                            return (
                                <Marker
                                    key={point.servicePointId}
                                    position={[point.latitude, point.longitude]}
                                    icon={isSelected ? selectedMarkerIcon : defaultMarkerIcon}
                                    zIndexOffset={isSelected ? 1000 : 0}
                                    eventHandlers={{
                                        click: () => setHighlightedId(point.servicePointId),
                                    }}
                                />
                            );
                        })}
                    </MapContainer>
                    {mappablePoints.length === 0 && !props.loading && (
                        <div
                            style={{
                                position: 'absolute',
                                left: 12,
                                bottom: 12,
                                zIndex: 1000,
                                background: 'rgba(255,255,255,0.92)',
                                borderRadius: 8,
                                padding: '8px 12px',
                                maxWidth: 320,
                                fontSize: 13,
                            }}
                        >
                            {translate('orderDetail.dhlMapPanHint')}
                        </div>
                    )}
                    {props.loading && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                zIndex: 1000,
                                background: 'rgba(255,255,255,0.9)',
                                borderRadius: 8,
                                padding: 8,
                            }}
                        >
                            <CircularProgress size={28} />
                        </div>
                    )}
                </div>
                <div
                    style={{
                        marginTop: 12,
                        padding: '12px 14px',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        background: '#fafafa',
                        minHeight: 72,
                    }}
                >
                    {highlightedPoint ? (
                        <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{highlightedPoint.name}</div>
                            <div style={{ fontSize: 14, color: '#444' }}>
                                {highlightedPoint.street}
                                <br />
                                {highlightedPoint.postalCode} {highlightedPoint.city}
                                {highlightedPoint.countryCode ? `, ${highlightedPoint.countryCode}` : ''}
                            </div>
                            <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                                {translate('orderDetail.dhlServicePointId')}: {highlightedPoint.servicePointId}
                            </div>
                            {highlightedPoint.openingHours ? (
                                <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                                    {translate('orderDetail.dhlOpeningHours')}: {highlightedPoint.openingHours}
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div style={{ fontSize: 14, color: '#666' }}>
                            {translate('orderDetail.dhlMapClickMarkerHint')}
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default DhlServicePointMap;
